<template>
  <div class="table-responsive">
    <table class="table" style="width: 100%;">
      <thead>
        <tr>
          <th>{{ $t("audit-control.control.no") }}</th>
          <th>{{ $t("audit-control.control.name") }}</th>
          <th>{{ $t("audit-control.control.class") }}</th>
          <th>{{ $t("audit-control.control.category") }}</th>
          <th>{{ $t("audit-control.control.criticality") }}</th>
          <th>{{ $t("audit-control.control.status") }}</th>
          <th>{{ $t("audit-control.control.type") }}</th>
          <th>{{ $t("compliance-item.compliance") }}</th>
          <th>{{ $t("audit-control.control.last-test") }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody v-if="pagination && pagination.docs.length">
        <tr v-for="item in pagination.docs" :key="item._id" class="hider">
          <td>
            <div>{{ item.no }}</div>
          </td>
          <td>
            <div>{{ item.name | translate | summary }}</div>
          </td>
          <td>{{ item.class ? $t("audit-control.class." + item.class.toLowerCase()) : "" }}</td>
          <td>
            <span v-if="item.category">{{ item.category.name | translate }}</span>
          </td>
          <td>
            <b-badge :variant="getCriticalityVariant(item.criticality)">
              {{ item.criticality ? $t("audit-control.criticality." + item.criticality.toLowerCase()) : "" }}
            </b-badge>
          </td>
          <td>{{ item.status ? $t("audit-control.status." + item.status.toLowerCase()) : "" }}</td>
          <td>
            <span v-if="item.type">{{ item.type.name | translate }}</span>
          </td>
          <td style="max-width: 300px;">
            <div v-if="item.complianceItems && item.complianceItems.length > 0">
              <div v-for="item in getDisplayItems(item)" :key="item._id" class="mb-1">
                <b-badge variant="light" class="text-wrap text-left border w-100 d-block">
                  <span class="small mr-1">{{ item.compliance }}</span>
                  <span v-if="item.no" class="font-weight-bold mr-1">{{ item.no }}</span>
                  <span class="small text-primary">{{ item.name | translate }}</span> -
                  <span class="small">{{ item.description | translate | summary(200) }}</span>
                </b-badge>
              </div>
              <div v-if="getHiddenCount(item) > 0">
                <b-badge :id="'popover-target-' + item._id" variant="light" class="small text-muted font-italic pl-1 cursor-pointer border">
                  + {{ getHiddenCount(item) }} {{ $t("common.more") }}
                </b-badge>

                <b-popover :target="'popover-target-' + item._id" triggers="hover focus" placement="auto" boundary="viewport" custom-class="shadow-sm">
                  <template #title>{{ $t("compliance-item.compliance") }}</template>
                  <div style="max-height: 300px; overflow-y: auto; max-width: 300px">
                    <div v-for="(items, standard) in getHiddenItemsGrouped(item)" :key="standard" class="mb-2">
                      <div class="font-weight-bold text-primary border-bottom mb-1 pb-1 small">{{ standard }}</div>
                      <div v-for="item in items" :key="item._id" class="mb-1 pl-1">
                        <b-badge variant="light" class="text-wrap text-left w-100 d-block border-0 p-0 text-dark font-weight-normal">
                          <small v-if="item.no" class="font-weight-bold mr-1">{{ item.no }}:</small>
                          <span class="text-primart">{{ item.name | translate }}</span> - <span class="">{{ item.description | translate | summary(200) }}</span>
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </b-popover>
              </div>
            </div>
          </td>

          <td>
            <div v-if="item.lastTest" class="small">
              <div class="font-weight-bold">{{ item.lastTest.auditName | translate }}</div>
              <div>{{ item.lastTest.testDate | date }}</div>
              <div v-if="item.lastTest.result">
                <b-badge :variant="item.lastTest.result === 'EFFECTIVE' ? 'success' : 'danger'">
                  {{ $t("audit-control.test.result." + item.lastTest.result.toLowerCase()) }}
                </b-badge>
                <span class="ml-1 text-muted" v-if="item.lastTest.findingCount > 0"> ({{ item.lastTest.findingCount }} {{ $t("audit-control.finding.title") }}) </span>
              </div>
            </div>
            <span v-else class="text-muted">-</span>
          </td>
          <td>
            <div class="d-flex" style="gap: 10px;">
              <i class="iconoir-edit hand" @click="$emit('edit', item)"></i>
              <!-- <i class="iconoir-trash hand" @click="$emit('delete', item._id)"></i> -->
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  props: {
    pagination: {
      type: Object,
      required: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    filters() {
      return this.$store.getters["organizationControl/controlFilters"] || {};
    },
  },
  methods: {
    getCriticalityVariant(criticality) {
      switch (criticality) {
        case "LOW":
          return "success";
        case "MEDIUM":
          return "warning";
        case "HIGH":
          return "danger";
        case "CRITICAL":
          return "dark";
        default:
          return "primary";
      }
    },
    getFilteredItems(control) {
      if (!control.complianceItems) return [];

      const { compliance, complianceItemNo, complianceItemSearch } = this.filters;
      const hasFilter = compliance || complianceItemNo || complianceItemSearch;

      if (!hasFilter) return control.complianceItems;

      return control.complianceItems.filter((item) => {
        let match = true;
        if (compliance && item.compliance !== compliance) match = false;

        if (match && complianceItemNo) {
          const noStr =
            typeof complianceItemNo === "object" && complianceItemNo.name ? complianceItemNo.name.tr || complianceItemNo.name.en || complianceItemNo.name : complianceItemNo;
          if (!item.no || !item.no.toLowerCase().includes(String(noStr).toLowerCase())) match = false;
        }

        if (match && complianceItemSearch) {
          const search = complianceItemSearch.toLowerCase();
          const nameTr = item.name && item.name.tr ? item.name.tr.toLowerCase() : "";
          const nameEn = item.name && item.name.en ? item.name.en.toLowerCase() : "";

          if (!nameTr.includes(search) && !nameEn.includes(search)) match = false;
        }
        return match;
      });
    },
    getDisplayItems(control) {
      const items = this.getFilteredItems(control);
      return items.slice(0, 3);
    },
    getHiddenCount(control) {
      if (!control.complianceItems) return 0;
      const displayedCount = this.getDisplayItems(control).length;
      return Math.max(0, control.complianceItems.length - displayedCount);
    },
    getHiddenItemsGrouped(control) {
      if (!control.complianceItems) return {};

      const displayedIds = this.getDisplayItems(control).map((i) => i._id);
      const hiddenItems = control.complianceItems.filter((i) => !displayedIds.includes(i._id));

      // Group by compliance
      const grouped = {};
      hiddenItems.forEach((item) => {
        const standard = item.compliance || "Other";
        if (!grouped[standard]) grouped[standard] = [];
        grouped[standard].push(item);
      });

      return grouped;
    },
  },
};
</script>
